'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Eye, EyeOff, Lock, Mail, AlertCircle, ShieldCheck, ArrowLeft, LayoutDashboard } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

// Hardcoded admin email allowlist — role check from user_profiles is the primary gate
const ADMIN_EMAILS = ['admin@zivasol.com'];

export default function AdminSignInClient() {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const normalizedEmail = email.trim().toLowerCase();

    try {
      const supabase = createClient();

      // Clear any existing session
      await supabase.auth.signOut();

      // Attempt direct sign in
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password: password,
      });

      if (!signInError && data?.user) {
        // Check role from user_profiles (primary check)
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', data.user.id)
          .maybeSingle();

        const isAdmin = profile?.role === 'admin' || ADMIN_EMAILS.includes(normalizedEmail);

        if (!isAdmin) {
          await supabase.auth.signOut();
          setError('Access denied. This portal is for administrators only.');
          setLoading(false);
          return;
        }

        // Ensure user_profiles record exists with admin role
        await supabase.from('user_profiles').upsert({
          id: data.user.id,
          email: normalizedEmail,
          full_name: profile?.role ? undefined : 'Ziva Admin',
          role: 'admin',
        }, { onConflict: 'id' });

        window.location.href = '/admin-dashboard';
        return;
      }

      // Sign in failed — try to provision the account via API
      const provisionRes = await fetch('/api/auth/provision-admin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail, password }),
      });

      const provisionData = await provisionRes.json();

      if (!provisionRes.ok || provisionData.error) {
        if (signInError?.message?.toLowerCase().includes('invalid') || signInError?.message?.toLowerCase().includes('credentials')) {
          setError('Invalid credentials. Please check your email and password.');
        } else if (signInError?.message?.toLowerCase().includes('email not confirmed')) {
          setError('Account not confirmed. Please contact your system administrator.');
        } else {
          setError(provisionData.error || 'Sign in failed. Please verify your credentials and try again.');
        }
        setLoading(false);
        return;
      }

      // Retry sign in after provisioning
      await supabase.auth.signOut();
      const { data: retryData, error: retryError } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password: password,
      });

      if (retryError || !retryData?.user) {
        setError('Account was set up but sign in failed. Please try again with the correct password.');
        setLoading(false);
        return;
      }

      // Ensure profile exists
      await supabase.from('user_profiles').upsert({
        id: retryData.user.id,
        email: normalizedEmail,
        full_name: 'Ziva Admin',
        role: 'admin',
      }, { onConflict: 'id' });

      window.location.href = '/admin-dashboard';
    } catch {
      setError('An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between w-[420px] xl:w-[480px] flex-shrink-0 bg-foreground p-10 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-white/5" />
          <div className="absolute -bottom-16 right-10 w-64 h-64 rounded-full bg-primary/20" />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 rounded-xl overflow-hidden bg-white/10">
              <Image src="/assets/images/logo-1778027352072.jpeg" alt="Ziva Niara Solutions logo" width={48} height={48} className="w-full h-full object-cover" />
            </div>
            <div>
              <span className="block text-lg font-bold text-white">Ziva DocuSecure</span>
              <span className="block text-xs text-white/50">Niara Solutions</span>
            </div>
          </div>
          <div className="mb-8">
            <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-5">
              <ShieldCheck size={28} className="text-accent" />
            </div>
            <h2 className="text-3xl font-bold text-white leading-tight mb-3">Admin Portal</h2>
            <p className="text-white/60 text-sm leading-relaxed">
              Authorised administrators only. Access all student records, documents, verification reports, exports, notifications, and settings.
            </p>
          </div>
          <div className="space-y-3">
            {[
              'View and manage all student submissions',
              'Review and verify uploaded documents',
              'Generate reports and export data',
              'Send notifications to students',
              'Manage programmes, cohorts, and settings',
            ].map((f) => (
              <div key={f} className="flex items-center gap-2.5">
                <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                </div>
                <span className="text-sm text-white/70">{f}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10">
          <div className="h-px bg-white/10 mb-4" />
          <p className="text-xs text-white/30">docusecure.myzivasol.com · © 2026 Ziva Niara Solutions</p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10 overflow-y-auto">
        <div className="w-full max-w-md">
          <Link href="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-8">
            <ArrowLeft size={14} /> Back to Home
          </Link>

          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-lg overflow-hidden bg-primary/10">
              <Image src="/assets/images/logo-1778027352072.jpeg" alt="Ziva Niara Solutions logo" width={36} height={36} className="w-full h-full object-cover" />
            </div>
            <div>
              <span className="block text-sm font-bold text-primary">Ziva DocuSecure</span>
              <span className="block text-[10px] text-muted-foreground">Niara Solutions</span>
            </div>
          </div>

          <div className="mb-7">
            <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-3 py-1 mb-4">
              <ShieldCheck size={12} className="text-amber-600" />
              <span className="text-xs font-semibold text-amber-700">Restricted Access</span>
            </div>
            <h1 className="text-2xl font-bold text-foreground">Admin Sign In</h1>
            <p className="text-sm text-muted-foreground mt-1">Authorised personnel only</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-start gap-2.5 p-3.5 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle size={15} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div>
              <label className="form-label">Admin Email Address</label>
              <div className="relative">
                <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input pl-9"
                  placeholder="admin@zivasol.com"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label className="form-label">Password</label>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input pl-9 pr-10"
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full py-2.5">
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <LayoutDashboard size={15} /> Access Admin Dashboard
                </span>
              )}
            </button>
          </form>

          <div className="mt-5 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <ShieldCheck size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Admin access is granted based on your role in the system. Contact your system administrator if you need access.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
